import { useEffect, useState } from 'react';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';
import { first } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { useActiveUserContext, useDataPackageApiContext, useStudiesApiContext } from '@vivli/core/infrastructure/context';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { PermissionsEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { useTranslation } from 'react-i18next';
import { getContentUnavailableMessage } from '@vivli/shared/infrastructure/constants';
import { LoggerService } from '@vivli/shared/infrastructure/service';

export function useStudyWithUserCheck(studyId: string, fromDoi?: boolean, needUsedIn?: boolean) {
    const [study, setStudy] = useState<IStudy>();
    const [studyUsedIn, setStudyUsedIn] = useState<string>(null); //string listing data requests studies used in
    const [studyIpdDataPackageSubmitted, setStudyIpdDataPackageSubmitted] = useState<boolean>(false); //is IPD data package submitted
    const [loadFailed, setLoadFailed] = useState(false); //the study itself could not be loaded

    const studiesService = useStudiesApiContext();
    const dataPackageService = useDataPackageApiContext();
    const { user, orgHasPermission, hasPermission, hasAnyPermission } = useActiveUserContext();
    const modalService = useModalApiContext();
    const { t } = useTranslation();

    const loadFailedMessage = getContentUnavailableMessage(t(TranslationKeyEnum.study));

    //The study could not be loaded at all - it has been withdrawn (unposted),
    //removed from the platform, or the call failed. Tell the user, and let
    //callers show a message instead of spinning forever on an undefined
    //study. DEV-397.
    const handleStudyLoadError = (error: string) => {
        LoggerService.warn(`[Study] unable to load study ${studyId}`, error);
        setLoadFailed(true);
        modalService.error(loadFailedMessage);
    };

    //The DOI lookup answers with specific server text ("Could not find metadata
    //for the DOI specified", a malformed-stem BadRequest, ...). That is the only
    //triage detail anyone gets from a report about a broken DOI link, so keep
    //showing it rather than replacing it with the generic sentence.
    const handleDoiLoadError = (error: string) => {
        LoggerService.warn(`[Study] unable to load study from DOI ${studyId}`, error);
        setLoadFailed(true);
        modalService.error(`Unable to load study from DOI:${error}`);
    };

    //A refusal here is expected for anyone outside the permitted set and does not
    //stop the study itself from displaying, so log it rather than raising a dialog.
    const handleUsedInError = (error: string) => {
        LoggerService.warn(`[Study] unable to load "used in" information for study ${studyId}`, error);
    };

    useEffect(() => {
        if (!studyId) {
            return;
        }

        //clear the previous study as well as the flag - otherwise a failed load
        //after a successful one leaves the last study's content on screen
        //underneath a message saying this one is unavailable
        setStudy(undefined);
        setLoadFailed(false);

        const subscriptions: Subscription[] = [];

        if (!fromDoi) {
            //normal case where ID used is study ID
            if (user) {
                subscriptions.push(
                    studiesService.getStudy(studyId).pipe(first()).subscribe({ next: setStudy, error: handleStudyLoadError })
                );
                //check to see if "usedIn" string is required along with study access
                const isOwner = orgHasPermission(study?.orgId, PermissionsEnum.ProviderActAdv);
                const wantsUsedIn = hasPermission(PermissionsEnum.EditStudyAdv) || isOwner || needUsedIn;
                //the endpoint is restricted to Vivli Admins and data contributor org
                //members, so asking as anybody else is a guaranteed refusal on every
                //study-details view. hasAnyPermission matches org memberships too,
                //which is the scope the server checks.
                const canQueryUsedIn = hasAnyPermission([PermissionsEnum.DrActAdv, PermissionsEnum.DataContrib]);
                if (wantsUsedIn && canQueryUsedIn) {
                    subscriptions.push(
                        studiesService.getUsedIn(studyId).pipe(first()).subscribe({ next: setStudyUsedIn, error: handleUsedInError })
                    );
                }
            } else {
                //anonymous access allowed without logged in user
                subscriptions.push(
                    studiesService.getStudyAnonymous(studyId).pipe(first()).subscribe({ next: setStudy, error: handleStudyLoadError })
                );
            }
        } else {
            //ID in this case comes from the DOI, not the study id
            subscriptions.push(
                studiesService.getStudyFromDoiId(studyId).pipe(first()).subscribe({ next: setStudy, error: handleDoiLoadError })
            );
        }

        //the modal provider sits above the router, so a request that fails after
        //the user has navigated away would otherwise pop this study's error
        //dialog on top of whatever page they moved to
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    }, [studyId]);

    useEffect(() => {
        if (study === null || study === undefined) {
            return;
        }
        if (study.ipdDataPackageId) {
            dataPackageService.getDataPackageSubmitStatus(study.ipdDataPackageId).pipe(first()).subscribe(setStudyIpdDataPackageSubmitted);
        } else {
            // for 8187, if studyIPD DataPackage does not exist, and an OrgId is in place, then create the missing IPD package document
            if (study.orgId) {
                studiesService.createIpdDataPackage(studyId).pipe(first()).subscribe(setStudy);
            }
        }
    }, [study]);

    return {
        study,
        setStudy,
        studyUsedIn,
        studyIpdDataPackageSubmitted,
        setStudyIpdDataPackageSubmitted,
        loadFailed,
        loadFailedMessage
    };
}
